import { useState, memo, useMemo } from 'react';
import type { VisitRecord, ClinicalTreatment } from '../../types/records';

interface SeanceHistoryTableProps {
  seances: VisitRecord[];
  grandTotalDue: number;
  grandTotalPaid: number;
  grandTotalRemaining: number;
  onToothClick?: (toothNumber: number) => void;
  onDeleteTreatment?: (id: string) => void;
  className?: string;
}

const SeanceRow = memo(function SeanceRow({
  seance,
  index,
  totalSeances,
  onToothClick,
  onDeleteTreatment,
}: {
  seance: VisitRecord;
  index: number;
  totalSeances: number;
  onToothClick?: (toothNumber: number) => void;
  onDeleteTreatment?: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(seance.status === 'OPEN');

  const isOpen = seance.status === 'OPEN';
  const due = Number(seance.amount_due) || Number(seance.total_cost) || 0;
  const paid = Number(seance.amount_paid) || 0;
  const remaining = Math.max(0, due - paid);
  const seanceNum = totalSeances - index;
  const date = new Date(seance.visit_date);

  return (
    <div className={`rounded-lg border transition-colors ${
      isOpen
        ? 'border-teal-300 dark:border-teal-700/30 bg-teal-50 dark:bg-teal-900/10 ring-1 ring-teal-200 dark:ring-teal-500/10'
        : 'border-border bg-muted/50 hover:bg-muted'
    }`}>
      {/* Séance header (clickable) — 2-row layout */}
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="w-full px-2.5 py-2 text-left"
      >
        {/* Row 1: chevron + number + date + status */}
        <div className="flex items-center gap-1.5">
          <svg
            className={`w-3 h-3 text-slate-500 transition-transform shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>

          <span className="text-[10px] font-mono font-semibold text-slate-500 dark:text-slate-400 shrink-0">
            #{seanceNum}
          </span>

          <span className="text-[10px] text-slate-500 dark:text-slate-300 shrink-0">
            {date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })}
          </span>

          {isOpen ? (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 border border-teal-300 dark:border-teal-700/30 shrink-0">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-500" />
              </span>
              <span className="text-[9px] font-semibold text-teal-600 dark:text-teal-400">EN COURS</span>
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700/40 text-[9px] font-medium text-slate-500 shrink-0">
              TERMINÉE
            </span>
          )}

          <span className="text-[10px] text-slate-500 ml-auto shrink-0">
            {(seance.treatments?.length ?? 0)} acte{(seance.treatments?.length ?? 0) !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Row 2: amounts — evenly distributed */}
        <div className="flex items-center gap-1 mt-1 ml-5">
          <span className="flex-1 text-center text-[10px] font-mono text-muted-foreground py-0.5 rounded bg-muted">
            {due > 0 ? `${due.toFixed(0)}` : '—'} <span className="text-[8px] text-slate-500 dark:text-slate-600">DHS</span>
          </span>
          <span className="flex-1 text-center text-[10px] font-mono text-emerald-600 dark:text-emerald-500 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/10">
            {paid > 0 ? `${paid.toFixed(0)}` : '—'} <span className="text-[8px] text-emerald-700 dark:text-emerald-700">DHS</span>
          </span>
          <span className={`flex-1 text-center text-[10px] font-mono font-semibold py-0.5 rounded ${
            remaining > 0 ? 'text-orange-600 dark:text-amber-400 bg-orange-50 dark:bg-amber-900/10' : 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/10'
          }`}>
            {remaining > 0 ? `${remaining.toFixed(0)}` : '0'} <span className={`text-[8px] ${remaining > 0 ? 'text-orange-700 dark:text-amber-700' : 'text-emerald-700'}`}>DHS</span>
          </span>
        </div>
      </button>

      {/* Expanded: treatment list */}
      {expanded && seance.treatments && seance.treatments.length > 0 && (
        <div className="px-2 pb-2 pt-1 space-y-px overflow-hidden">
          {/* Column headers */}
          <div className="flex items-center gap-1.5 px-2 py-1 text-[9px] uppercase tracking-widest text-slate-600 font-semibold">
            <span className="w-7 shrink-0">N°</span>
            <span className="w-12 shrink-0">Code</span>
            <span className="flex-1">Acte</span>
            <span className="w-14 shrink-0 text-right">Montant</span>
          </div>
          {seance.treatments.map((t: ClinicalTreatment) => (
            <div key={t.id} className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-muted/50 hover:bg-muted transition-colors border border-transparent hover:border-border">
              {t.tooth_number ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onToothClick?.(t.tooth_number!); }}
                  className="w-7 shrink-0 text-[10px] font-mono font-bold text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-left transition-colors"
                >
                  {t.tooth_number}
                </button>
              ) : (
                <span className="w-7 shrink-0 text-[10px] text-slate-600">—</span>
              )}
              <span className="w-12 shrink-0 text-[10px] font-mono text-slate-500 truncate">{t.act_code}</span>
              <span className="flex-1 text-[10px] text-slate-700 dark:text-slate-300 truncate min-w-0" title={t.act_label}>
                {t.act_label}
                {t.surfaces && <span className="text-slate-600 ml-1">({t.surfaces})</span>}
              </span>
              <span className="w-14 shrink-0 text-[10px] font-mono text-slate-500 dark:text-slate-400 text-right" title={`${Number(t.amount || 0).toFixed(2)} DHS`}>
                {Number(t.amount || 0) > 0 ? `${Number(t.amount || 0).toFixed(0)}` : '—'}
              </span>
              {isOpen && onDeleteTreatment && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteTreatment(t.id); }}
                  className="w-6 shrink-0 flex items-center justify-center text-red-500/60 hover:text-red-400 transition-colors"
                  title="Annuler cet acte"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {expanded && (!seance.treatments || seance.treatments.length === 0) && (
        <div className="px-3 pb-2 pt-0.5">
          <p className="text-[10px] text-slate-600 italic px-2">Aucun acte enregistré</p>
        </div>
      )}
    </div>
  );
});

export default function SeanceHistoryTable({
  seances,
  grandTotalDue,
  grandTotalPaid,
  grandTotalRemaining,
  onToothClick,
  onDeleteTreatment,
  className = '',
}: SeanceHistoryTableProps) {
  const sortedSeances = useMemo(() => {
    return [...seances].sort((a, b) => {
      if (a.status === 'OPEN' && b.status !== 'OPEN') return -1;
      if (b.status === 'OPEN' && a.status !== 'OPEN') return 1;
      return new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime();
    });
  }, [seances]);

  if (seances.length === 0) {
    return (
      <div className={`flex items-center justify-center py-6 ${className}`}>
        <p className="text-[11px] text-slate-600">Aucune séance enregistrée</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col overflow-hidden min-w-0 ${className}`}>
      {/* Header — stacked layout to prevent overflow in narrow panel */}
      <div className="shrink-0 px-3 pt-3 pb-2 border-b border-border space-y-2">
        <h4 className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
          Historique des séances
        </h4>
        {/* Grand totals — 3-column grid, contained */}
        <div className="grid grid-cols-3 gap-1.5 min-w-0">
          <div className="flex flex-col items-center px-1.5 py-1.5 rounded-lg bg-muted border border-border overflow-hidden">
            <span className="text-[9px] uppercase tracking-wider text-slate-500 dark:text-slate-600 font-medium">Total</span>
            <span className="text-[12px] font-mono font-bold text-slate-800 dark:text-slate-200 leading-tight truncate w-full text-center">{grandTotalDue.toFixed(0)}</span>
            <span className="text-[8px] text-slate-600 font-medium">DHS</span>
          </div>
          <div className="flex flex-col items-center px-1.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/20 overflow-hidden">
            <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-medium">Payé</span>
            <span className="text-[12px] font-mono font-bold text-emerald-600 dark:text-emerald-400 leading-tight truncate w-full text-center">{grandTotalPaid.toFixed(0)}</span>
            <span className="text-[8px] text-emerald-600 dark:text-emerald-700 font-medium">DHS</span>
          </div>
          <div className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg border overflow-hidden ${
            grandTotalRemaining > 0
              ? 'bg-orange-50 dark:bg-amber-900/15 border-orange-300 dark:border-amber-800/20'
              : 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800/20'
          }`}>
            <span className={`text-[9px] uppercase tracking-wider font-medium ${
              grandTotalRemaining > 0 ? 'text-orange-700 dark:text-amber-700' : 'text-emerald-700'
            }`}>Reste</span>
            <span className={`text-[12px] font-mono font-bold leading-tight truncate w-full text-center ${
              grandTotalRemaining > 0 ? 'text-orange-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
            }`}>{grandTotalRemaining.toFixed(0)}</span>
            <span className={`text-[8px] font-medium ${
              grandTotalRemaining > 0 ? 'text-orange-600 dark:text-amber-700' : 'text-emerald-600 dark:text-emerald-700'
            }`}>DHS</span>
          </div>
        </div>
      </div>

      {/* Séance list */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5 scrollbar-thin min-w-0">
        {sortedSeances.map((seance, i) => (
          <SeanceRow
            key={seance.id}
            seance={seance}
            index={i}
            totalSeances={sortedSeances.length}
            onToothClick={onToothClick}
            onDeleteTreatment={onDeleteTreatment}
          />
        ))}
      </div>
    </div>
  );
}
